import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight, HeartHandshake, Globe2 } from "lucide-react";
import Layout from "../components/Layout";
import api from "../lib/api";
import TierBadge from "../components/TierBadge";

/**
 * Iter-27.63 — Hub Espace Partenaires (vue ambassadeur).
 *
 * Cette page liste TOUTES les associations partenaires visibles par
 * l'ambassadeur, dans une UX premium séparée de /associations (formation).
 *
 * Une asso n'apparaît ici QUE si son `tier` est défini et que la feature
 * "espace partenaire" est activée côté admin (defaults : true).
 */
export default function PartnerSpacesHub() {
    const [items, setItems] = useState(null);

    useEffect(() => {
        api.get("/associations")
            .then((r) => setItems(r.data || []))
            .catch(() => setItems([]));
    }, []);

    if (items === null) {
        return (
            <Layout title="Espace Partenaires" subtitle="L'humain derrière chaque don">
                <div className="glass-card rounded-3xl p-10 text-center text-white/50">
                    Chargement…
                </div>
            </Layout>
        );
    }

    return (
        <Layout title="Espace Partenaires" subtitle="L'humain derrière chaque don">
            <div className="mb-6 max-w-3xl">
                <div className="inline-flex items-center gap-2 bg-shariti-yellow/15 border border-shariti-yellow/30 rounded-full px-3 py-1.5 mb-3">
                    <Sparkles className="w-3.5 h-3.5 text-shariti-yellow" />
                    <span className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-shariti-yellow">
                        Nouveau
                    </span>
                </div>
                <p className="text-sm sm:text-base text-white/70 font-light leading-relaxed">
                    Chaque association partenaire dispose d'un espace dédié pour te partager ses{" "}
                    <span className="text-shariti-yellow font-bold">messages de remerciement</span>{" "}
                    et ses <span className="text-shariti-orange-light font-bold">histoires d'impact</span> réelles, directement depuis le terrain. Découvre l'humain derrière chaque don que tu obtiens.
                </p>
            </div>

            {items.length === 0 ? (
                <div className="glass-card rounded-3xl p-10 text-center text-white/55">
                    <HeartHandshake className="w-10 h-10 text-shariti-yellow/50 mx-auto mb-3" />
                    <p className="font-bold text-white/70 mb-1">
                        Aucun espace partenaire pour le moment
                    </p>
                    <p className="text-sm">Les associations te seront proposées au fil de l'eau.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map((a, idx) => (
                        <PartnerCard key={a.association_id} a={a} idx={idx} />
                    ))}
                </div>
            )}
        </Layout>
    );
}

function PartnerCard({ a, idx }) {
    const accent = a.primary_color || "#EE722E";
    const hero = a.hero_image_url || a.cover_image;
    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: idx * 0.05 }}
        >
            <Link
                to={`/espace-partenaires/${a.association_id}`}
                data-testid={`partner-card-${a.association_id}`}
                className="group relative block rounded-3xl overflow-hidden bg-white/3 border border-white/10 hover:border-white/20 transition-all"
            >
                <div className="relative aspect-[16/9] overflow-hidden">
                    {hero ? (
                        <img
                            src={hero}
                            alt={a.name}
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                    ) : (
                        <div
                            className="absolute inset-0"
                            style={{
                                background: `linear-gradient(135deg, ${accent} 0%, ${accent}55 100%)`,
                            }}
                        />
                    )}
                    <div
                        className="absolute inset-0"
                        style={{
                            background: `linear-gradient(180deg, transparent 0%, ${accent}33 50%, rgba(24,24,24,0.9) 100%)`,
                        }}
                    />
                    {a.tier && (
                        <div className="absolute top-3 left-3">
                            <TierBadge tier={a.tier} size="sm" />
                        </div>
                    )}
                    {a.logo_url && (
                        <div className="absolute bottom-3 right-3 w-12 h-12 rounded-2xl bg-white p-1 flex items-center justify-center shadow-2xl">
                            <img
                                src={a.logo_url}
                                alt={a.name}
                                className="w-full h-full object-contain"
                            />
                        </div>
                    )}
                </div>
                <div className="p-4 sm:p-5">
                    <h3 className="text-base sm:text-lg font-extrabold text-white leading-tight mb-1 tracking-tight">
                        {a.name}
                    </h3>
                    {(a.tagline || a.short_description) && (
                        <p className="text-xs text-white/55 font-light leading-snug line-clamp-2">
                            {a.tagline || a.short_description}
                        </p>
                    )}
                    <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-[0.18em] text-shariti-orange-light group-hover:text-shariti-orange transition-colors">
                        Découvrir l'espace
                        <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                    </div>
                </div>
            </Link>
        </motion.div>
    );
}
